import { useState, useEffect, useCallback } from "react";
import type { Zelt } from "@/data/zelte";

const STORAGE_KEY = "zelt-vergleich";
const MAX_ITEMS = 4;

export function useVergleich() {
  const [vergleichIds, setVergleichIds] = useState<string[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(vergleichIds));
    } catch {
      // Ignore storage errors
    }
  }, [vergleichIds]);

  const addToVergleich = useCallback((zelt: Zelt) => {
    setVergleichIds(prev => {
      if (prev.includes(zelt.id)) return prev;
      if (prev.length >= MAX_ITEMS) {
        // Remove first item and add new one
        return [...prev.slice(1), zelt.id];
      }
      return [...prev, zelt.id];
    });
  }, []);

  const removeFromVergleich = useCallback((id: string) => {
    setVergleichIds(prev => prev.filter(i => i !== id));
  }, []);

  const toggleVergleich = useCallback((zelt: Zelt) => {
    if (vergleichIds.includes(zelt.id)) {
      removeFromVergleich(zelt.id);
    } else {
      addToVergleich(zelt);
    }
  }, [vergleichIds, addToVergleich, removeFromVergleich]);

  const clearVergleich = useCallback(() => {
    setVergleichIds([]);
  }, []);

  const replaceVergleich = useCallback((ids: string[]) => {
    const unique = [...new Set(ids.filter(Boolean))].slice(0, MAX_ITEMS);
    setVergleichIds(unique);
  }, []);

  const isInVergleich = useCallback((id: string) => {
    return vergleichIds.includes(id);
  }, [vergleichIds]);

  return {
    vergleichIds,
    addToVergleich,
    removeFromVergleich,
    toggleVergleich,
    clearVergleich,
    replaceVergleich,
    isInVergleich,
    count: vergleichIds.length,
  };
}
